import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Trash, ChatCircleDots, Phone, PaperPlaneTilt, ArrowRight } from "@phosphor-icons/react";

const CHANNEL_META = {
  whatsapp: { label: "WhatsApp", Icon: ChatCircleDots, cls: "text-success" },
  voice:    { label: "Voice Call", Icon: Phone, cls: "text-primary" },
  both:     { label: "WhatsApp + Call", Icon: PaperPlaneTilt, cls: "text-foreground" },
};

const TONE_META = {
  gentle: { label: "Gentle",  cls: "bg-success/10 text-success border-success/30" },
  firm:   { label: "Firm",    cls: "bg-amber-500/15 text-amber-700 dark:text-amber-400 border-amber-500/30" },
  final:  { label: "Final",   cls: "bg-destructive/15 text-destructive border-destructive/30" },
};

export default function RuleBuilder({ rules, onChange }) {
  const update = (idx, patch) => {
    const next = rules.map((r, i) => (i === idx ? { ...r, ...patch } : r));
    onChange(next);
  };
  const addRule = () => {
    const lastDay = rules.length ? Math.max(...rules.map(r => r.days_after_due)) : 0;
    onChange([...rules, { days_after_due: lastDay + 3, channel: "whatsapp", tone: "firm" }]);
  };
  const remove = (idx) => onChange(rules.filter((_, i) => i !== idx));

  const sorted = [...rules].sort((a, b) => a.days_after_due - b.days_after_due);

  return (
    <div className="space-y-3" data-testid="rule-builder">
      {sorted.length === 0 && (
        <div className="text-xs text-muted-foreground border border-dashed border-border rounded-md p-5 text-center">
          No rules yet. Add your first escalation step.
        </div>
      )}
      {sorted.map((rule, idx) => {
        const realIdx = rules.indexOf(rule);
        const meta = CHANNEL_META[rule.channel] || CHANNEL_META.whatsapp;
        const Icon = meta.Icon;
        return (
          <div
            key={realIdx}
            data-testid={`rule-row-${realIdx}`}
            className="group flex items-center gap-3 border border-border bg-background rounded-md p-3 hover:border-primary/40 transition-colors"
          >
            <div className="flex items-center gap-1.5 shrink-0 w-24">
              <span className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">Day</span>
              <Input
                data-testid={`rule-day-${realIdx}`}
                type="number"
                min={0}
                value={rule.days_after_due}
                onChange={(e) => update(realIdx, { days_after_due: parseInt(e.target.value || "0", 10) })}
                className="h-8 w-14 font-mono text-sm tabular-nums"
              />
            </div>

            <ArrowRight className="h-3.5 w-3.5 text-muted-foreground shrink-0" />

            <div className="flex items-center gap-1.5 shrink-0">
              <Icon className={`h-4 w-4 ${meta.cls}`} weight="duotone" />
              <Select value={rule.channel} onValueChange={(v) => update(realIdx, { channel: v })}>
                <SelectTrigger data-testid={`rule-channel-${realIdx}`} className="h-8 w-[150px] text-sm"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="whatsapp">WhatsApp</SelectItem>
                  <SelectItem value="voice">Voice Call</SelectItem>
                  <SelectItem value="both">WhatsApp + Call</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <ArrowRight className="h-3.5 w-3.5 text-muted-foreground shrink-0" />

            <Select value={rule.tone} onValueChange={(v) => update(realIdx, { tone: v })}>
              <SelectTrigger data-testid={`rule-tone-${realIdx}`} className={`h-8 w-[120px] text-sm border ${TONE_META[rule.tone]?.cls || ""}`}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="gentle">Gentle</SelectItem>
                <SelectItem value="firm">Firm</SelectItem>
                <SelectItem value="final">Final Notice</SelectItem>
              </SelectContent>
            </Select>

            <div className="flex-1" />

            <Button
              variant="ghost"
              size="sm"
              onClick={() => remove(realIdx)}
              data-testid={`rule-remove-${realIdx}`}
              className="h-8 w-8 p-0 opacity-0 group-hover:opacity-100 transition-opacity"
              aria-label="Remove rule"
            >
              <Trash className="h-4 w-4 text-destructive" />
            </Button>
          </div>
        );
      })}
      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={addRule}
        data-testid="rule-add-button"
        className="w-full border-dashed border-border hover:border-primary hover:bg-primary/5"
      >
        <Plus className="h-4 w-4 mr-1.5" /> Add escalation step
      </Button>
    </div>
  );
}
